import 'react-simple-phone-input/dist/style.css';

import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import { InputBaseProps } from '@mui/material/InputBase';
import { Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { useState } from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
  FieldValues,
} from 'react-hook-form';
import { PhoneInput, PhoneInputResponseType } from 'react-simple-phone-input';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { DEFAULT_COUNTRY_CODE } from '~constants/enums';
import { useStores } from '~store';
import { matchIsValidTelOrTestTel } from '~utils/utilFunctions';

interface PhoneCodeFlagInputProps {
  control: Control | undefined;
  errors: FieldErrors<FieldValues>;
  name: string;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
  inputProps?: InputBaseProps['inputProps'];
  sx?: SxProps;
  defaultCountryCode?: CountryCode | undefined;
}

const buttonStyle = (theme: Theme) => ({
  overflow: 'hidden',
  width: 'max-content',
  paddingRight: '6px', // Minimizing the width of the button
  height: '36px', // 38px is the height on the input fields.
  backgroundColor: theme.semantic.surfaceInputSolid,
  color: theme.semantic.textStrong,
});

const containerStyle = (
  invalid: boolean,
  required: boolean,
  disabled: boolean,
  theme: Theme,
) => ({
  position: 'relative',
  marginTop: '2px', // 2px margin to match Mui input fields
  boxShadow: invalid
    ? `0 0 0 2px ${theme.palette.error.main}`
    : required && !disabled
      ? `0 0 0 2px ${theme.palette.primary.main}`
      : `0 0 0 1px ${theme.palette.grey[500]}`, // Mid grey reads as a border against both
  // the light and the dark input fill; the semantic border tokens are tuned for
  // surfaces and drop to ~1.3:1 on white here.
  borderRadius: '4px',
  '&:hover': {
    boxShadow: invalid || required ? '' : `0 0 0 1px ${theme.semantic.textStrong}`,
  },
  '&:focus-within': {
    boxShadow: invalid ? `` : `0 0 0 2px ${theme.palette.primary.main}`,
    overflow: 'visible',
  },
  // The library hard-codes a translucent black wash that is invisible on a dark list.
  '& li:hover, & li.active': {
    backgroundColor: theme.semantic.actionHover,
  },
});

const dropdownStyle = (theme: Theme) => ({
  zIndex: 100,
  backgroundColor: theme.semantic.surfaceRaised,
  color: theme.semantic.textStrong,
  border: `1px solid ${theme.semantic.borderStrong}`,
});

const searchInputStyle = (theme: Theme) => ({
  backgroundColor: theme.semantic.surfaceInputSolid,
  color: theme.semantic.textStrong,
  border: `1px solid ${theme.semantic.borderStrong}`,
});

const inputStyle = (theme: Theme) => ({
  fontSize: '14px', // Standard font size for Mui input fields
  padding: '4px',
  // The underlying <input> defaults to a white/black browser style; drive it from
  // the theme so it matches the flag button and flips in dark mode.
  backgroundColor: theme.semantic.surfaceInputSolid,
  color: theme.semantic.textStrong,
});

const PhoneCodeFlagInput = ({
  control,
  name,
  label,
  isRequired,
  disabled = false,
  inputProps,
  sx,
  defaultCountryCode,
}: PhoneCodeFlagInputProps) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const fallbackCountry =
    (userStore.userCompany?.country as CountryCode) || DEFAULT_COUNTRY_CODE;
  const effectiveDefaultCountry = defaultCountryCode || fallbackCountry;
  const emptyValue = `+${getCountryCallingCode(effectiveDefaultCountry)}`;
  const [countryCode, setCountryCode] = useState<CountryCode>(effectiveDefaultCountry);

  const handlePhoneChange = (
    e: PhoneInputResponseType,
    field: Pick<ControllerRenderProps<FieldValues, string>, 'onChange'>,
  ) => {
    if (matchIsValidTelOrTestTel(e.value)) {
      const parsedNumber = parsePhoneNumberWithError(e.value);
      if (parsedNumber?.country && parsedNumber?.country !== countryCode) {
        setCountryCode(parsedNumber.country);
      }
    }

    if (e.value === emptyValue) {
      // When the user clears the input, the value collapses to '+<dial-code>'
      // for the currently-displayed country. Treat that as empty to avoid
      // spurious validation errors.
      field.onChange(' ');
    } else {
      field.onChange(e.value);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (value: string) => {
          const valueWithoutSpaces = value?.replace(/\s+/g, '') || '';
          if (
            valueWithoutSpaces.startsWith('+11') &&
            !valueWithoutSpaces.startsWith('+111')
          ) {
            return 'Remove leading 1 to save';
          }
          return matchIsValidTelOrTestTel(value)
            ? true
            : 'Please enter a valid phone number';
        },
      }}
      // need to destructure ref out of the props to avoid issues with forwarding refs
      // oxlint-disable-next-line @typescript-eslint/no-unused-vars
      render={({ field: { ref: _ref, ...field }, fieldState }) => (
        <FormControl
          sx={{ width: '100%', ...sx } as SxProps<Theme>}
          error={fieldState.invalid}
        >
          <FormFieldLabel label={`${label}${isRequired ? ' *' : ''}`}></FormFieldLabel>
          <Box
            sx={[
              {
                display: 'flex',
                flexDirection: 'column',
              },
              containerStyle(fieldState.invalid, isRequired || false, disabled, theme),
            ]}
          >
            <PhoneInput
              {...field}
              value={
                field.value && matchIsValidTelOrTestTel(field.value)
                  ? (parsePhoneNumberWithError(field?.value)?.nationalNumber as string)
                  : field.value
              }
              placeholder="9051231234"
              country={
                field.value &&
                matchIsValidTelOrTestTel(field.value) &&
                parsePhoneNumberWithError(field?.value)?.country
                  ? (parsePhoneNumberWithError(field.value).country as string)
                  : effectiveDefaultCountry
              }
              preferredCountries={['CA', 'US']}
              onChange={(e: PhoneInputResponseType) => {
                handlePhoneChange(e, field);
              }}
              inputProps={inputProps}
              inputStyle={inputStyle(theme)}
              buttonStyle={buttonStyle(theme)}
              dropdownStyle={dropdownStyle(theme)}
              searchInputStyle={searchInputStyle(theme)}
              showSearchIcon={false}
              showDropdownIcon={false}
              disableInput={disabled || false}
              disableDropdownOnly={disabled || false}
            />
            {matchIsValidTelOrTestTel((field.value as string) || '') ? (
              <CheckIcon
                style={{
                  color: 'green',
                  height: '16px',
                  width: '16px',
                  position: 'absolute',
                  right: '0',
                  top: '0',
                  transform: 'translateX(35%) translateY(-30%)',
                  // The badge overlaps the field edge, so its fill must be opaque to
                  // mask the border beneath it — the input token is transparent in light.
                  backgroundColor: theme.semantic.surfaceRaised,
                  border: `1px solid ${theme.semantic.borderStrong}`,
                  borderRadius: '50%',
                }}
              />
            ) : null}
          </Box>
          {fieldState.error && (
            <Typography
              variant="body2"
              color={'error'}
              sx={{
                paddingTop: '4px',
              }}
            >
              {fieldState.error.message}
            </Typography>
          )}
        </FormControl>
      )}
    />
  );
};
export { PhoneCodeFlagInput };
